import classNames from "classnames";
import { useMemo, useState } from "react";
import { AutocompleteOption } from "./types/types";
import { AppAutocomplete } from "../../shared/components/AppAutocomplete";
import { ArrowIcon } from "../../shared/icons/ArrowIcon";
import { AppLoading } from "../../shared/components/AppLoading";
import { AppTextField } from "../../shared/components/AppInput/AppTextField";
import styles from "./styles.module.css";
import { AutocompleteChangeReason } from "@mui/material";

interface Props<T> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getLabel: (option: T) => string;
  getValue: (option: T) => string | number;
  getOptionKey?: (option: T) => string | number;
  emptyOptionLabel?: string;
  autocompleteStyle: "select" | "custom";
  groupBy?: (option: T) => string;
  freeSolo?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
  loading?: boolean;
}

export const GenericAutocomplete = <T,>({
  options,
  value,
  onChange,
  getLabel,
  getValue,
  emptyOptionLabel,
  autocompleteStyle,
  getOptionKey,
  groupBy,
  placeholder = "Выберите значение...",
  isDisabled = false,
  freeSolo = false,
  className,
  loading = false,
}: Props<T>) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [isClearedByUser, setIsClearedByUser] = useState(false);

  const wrappedGetOptionKey = useMemo(() => {
    if (!getOptionKey) return undefined;

    return (option: AutocompleteOption<T>) => {
      if (option.isEmpty || !option.original) return "__EMPTY_OPTION__";
      return getOptionKey(option.original);
    };
  }, [getOptionKey]);

  const processedOptions = useMemo<AutocompleteOption<T>[]>(() => {
    const result: AutocompleteOption<T>[] = [];
    if (emptyOptionLabel !== undefined) {
      result.push({
        original: null,
        label: emptyOptionLabel,
        value: "__EMPTY_OPTION__",
        isEmpty: true,
      });
    }

    options.forEach((opt) => {
      result.push({
        original: opt,
        label: getLabel(opt),
        value: getValue(opt),
        isEmpty: false,
      });
    });

    return result;
  }, [options, emptyOptionLabel, getLabel, getValue]);

  const selectedOption = useMemo(() => {
    if (value === null) {
      if (isClearedByUser) return null;
      return processedOptions.find((o) => o.isEmpty) || null;
    }
    setIsClearedByUser(false);
    return (
      processedOptions.find((o) => !o.isEmpty && o.value === getValue(value)) ||
      null
    );
  }, [value, processedOptions, getValue, isClearedByUser]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: AutocompleteOption<T> | null,
    reason: AutocompleteChangeReason,
  ) => {
    if (reason === "clear") {
      setIsClearedByUser(true);
      onChange(null);
      return;
    }
    setIsClearedByUser(false);
    if (!newValue || newValue.isEmpty) {
      onChange(null);
    } else {
      onChange(newValue.original as T);
    }
  };

  const handleGroupBy = (option: AutocompleteOption<T>) => {
    if (option.isEmpty || !option.original) return "";
    return groupBy ? groupBy(option.original) : "";
  };

  return (
    <div className={styles[`${autocompleteStyle}-autocomplete-wrapper`]}>
      <AppAutocomplete
        freeSolo={freeSolo}
        disabled={isDisabled}
        loading={loading}
        className={classNames(
          styles["autocomplete-wrapper"],
          styles[`${autocompleteStyle}-autocomplete`],
          className,
        )}
        popupIcon={<ArrowIcon />}
        options={processedOptions}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        getOptionKey={wrappedGetOptionKey}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val?.value}
        groupBy={groupBy ? handleGroupBy : undefined}
        renderGroup={(params) => (
          <div key={params.key}>
            {params.group && (
              <div className={styles["group-placeholder"]}>{params.group}</div>
            )}
            <ul>{params.children}</ul>
          </div>
        )}
        renderInput={(params) => (
          <AppTextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <AppLoading settingSize={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            classes={{ root: styles[`${autocompleteStyle}-text-field`] }}
          />
        )}
      />
    </div>
  );
};
